import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight, Info, Plus, RefreshCw, RotateCcw, Save, X } from "lucide-react";
import { useAdapter, useTopology } from "@/store/site";
import { PageShell } from "./PageShell";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { pushToast } from "@/hooks/useToasts";
import type { ControllerMode, ScheduleDoc, ScheduleEntry, ScheduleSource, ScheduleStatus } from "@/data/types";
import { EmbeddedCtxSource } from "@/data/tb/embedded";
import { MODE_CATALOG, MODE_TONE, ORDERED_MODES, defaultParamsFor } from "@/data/modeCatalog";
import { cn } from "@/lib/cn";
import { tr, useT } from "@/lib/i18n";
import { carryTo } from "@/lib/carryParams";
import { markPending, resolvePending } from "@/store/pendingWrites";
import { ActiveStatePanel } from "@/components/schedule/ActiveStatePanel";
import { CalendarGrid } from "@/components/schedule/CalendarGrid";
import { DefaultEditor } from "@/components/schedule/DefaultEditor";
import { EntryEditor } from "@/components/schedule/EntryEditor";
import { EntryList } from "@/components/schedule/EntryList";
import { OverridePanel } from "@/components/schedule/OverridePanel";
import { StatusBanner } from "@/components/schedule/StatusBanner";
import { makeEntryId, projectInTz } from "@/components/schedule/scheduleLib";

/**
 * Schedule & modes — operator-facing surface for the controller-service's
 * `bess_schedule` and `bess_manual_override` SHARED_SCOPE attributes.
 *
 * The page is a thin composition of:
 *   1. Active-state strip — what's active, next change, override controls.
 *   2. Mode-reference cards — operator-facing description of every mode.
 *   3. Default mode editor — customisable catch-all the cloud user owns.
 *   4. Weekly calendar — interactive (click to edit, drag to shift / resize,
 *      click empty area to add).
 *   5. Entry list — table mirror with edit / clone / disable / delete.
 *   6. Status banner — confirms the edge has applied the last save.
 *
 * Save semantics:
 *   * Edits stream into a local `draft` doc with a `dirty` flag.
 *   * "Discard" reverts the draft to the last server-known doc.
 *   * "Save" pushes the full doc and stamps `meta.updatedAt`; the StatusBanner
 *     polls the edge's echo and lights up once `applied_updatedAt` matches.
 */
export default function SchedulePage() {
  const t = useT();
  const adapter = useAdapter();
  const siteId = useTopology().id;
  const [source, setSource] = useState<ScheduleSource | null>(null);
  const [serverDoc, setServerDoc] = useState<ScheduleDoc | null>(null);
  const [draft, setDraft] = useState<ScheduleDoc | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newEntryId, setNewEntryId] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  // R06 — when the OverridePanel is opened from an alarm's "Adjust dispatch"
  // lever (?override=1 with reason/alarmId), carry the alarm's reason + origin
  // so the panel prefills the reason field and shows a "from alarm" note.
  const [overrideOrigin, setOverrideOrigin] = useState<{ reason: string; fromAlarm: boolean }>({ reason: "", fromAlarm: false });
  const [expandedDesc, setExpandedDesc] = useState<ControllerMode | null>(null);
  // F13: collapse the mode-reference grid by default (pure reference depth).
  // In-memory only — NOT persisted to sessionStorage in this partial scope.
  const [modeRefOpen, setModeRefOpen] = useState(false);
  // N10: the priority-sorted table view (EntryList) is a collapsible
  // disclosure under the calendar — default CLOSED for the answer-first win,
  // but PERSISTED per-site in sessionStorage so a power user who opens it (the
  // table is the real priority/date-range authoring-scan surface) keeps it
  // open across visits / iframe remounts. Read lazily; same try/catch idiom as
  // the draft mirror above (sessionStorage throws in sandboxed iframes).
  const tableViewKey = `ems-schedule-tableview:${siteId}`;
  const [tableViewOpen, setTableViewOpen] = useState<boolean>(() => {
    try { return sessionStorage.getItem(tableViewKey) === "1"; } catch { return false; }
  });
  // Re-read the persisted flag on site switch (the key changed).
  useEffect(() => {
    try { setTableViewOpen(sessionStorage.getItem(tableViewKey) === "1"); } catch { setTableViewOpen(false); }
  }, [tableViewKey]);
  const toggleTableView = useCallback(() => {
    setTableViewOpen((o) => {
      const next = !o;
      try { sessionStorage.setItem(tableViewKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, [tableViewKey]);
  const [scheduleStatus, setScheduleStatus] = useState<ScheduleStatus | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<ScheduleStatus | null>(null);
  const lastSavedAtRef = useRef<number | null>(null);
  // F21: the pending-writes-store id of the last schedule save still awaiting
  // an edge echo. Resolved in the effect below from the SAME applied/rejected
  // condition StatusBanner uses; null when there's nothing in flight.
  const pendingScheduleIdRef = useRef<string | null>(null);
  // Track dirty / draft through refs so the refresh closure stays stable.
  // Including `draft` in the useCallback deps caused refresh to be re-
  // created — and therefore the polling interval to be torn down + reset —
  // on every keystroke; each chip click then triggered a fresh
  // schedule()+doc()+status()+override() fan-out, locking up the page.
  const dirtyRef = useRef(false);
  const hasDraftRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { hasDraftRef.current = draft !== null; }, [draft]);

  // ─── Draft protection (MKT-01 / PERS-02, item 3.5) ──────────────────
  // Layer 3 of the persistence rule: the dirty draft is mirrored into
  // sessionStorage on every change, so a tab close, an iframe re-mount or
  // an in-app navigation can never silently destroy unsaved work. The
  // mirror is cleared on successful save and on explicit Discard; on the
  // first refresh after mount it is restored (dirty=true) only when NEWER
  // than the server doc, behind a dismissible warn banner.
  const draftStorageKey = `ems-schedule-draft:${siteId}`;
  // When non-null: ms timestamp the restored mirror was last written —
  // drives the "Restored unsaved draft from HH:MM" banner.
  const [restoredAt, setRestoredAt] = useState<number | null>(null);
  // One-shot per mount (re-armed on site switch): the restore check must
  // not re-run on every 15-s refresh tick.
  const restoreCheckedRef = useRef(false);

  // In-app site switch while the page stays mounted: the in-memory draft
  // belongs to the PREVIOUS site. It is already mirrored under that
  // site's key (recoverable on return), so reset local state, re-arm the
  // restore check, and let refresh() repopulate for the new site. The
  // pending flag stops the mirror effect below from bleeding the old
  // draft into the new site's key during this transition commit (state
  // resets only apply on the next render, but effects fire now).
  const prevStorageKeyRef = useRef(draftStorageKey);
  const siteSwitchPendingRef = useRef(false);
  useEffect(() => {
    if (prevStorageKeyRef.current === draftStorageKey) return;
    prevStorageKeyRef.current = draftStorageKey;
    siteSwitchPendingRef.current = true;
    restoreCheckedRef.current = false;
    setDraft(null);
    setDirty(false);
    setRestoredAt(null);
    setEditingId(null);
    setNewEntryId(null);
    setSaveError(null);
  }, [draftStorageKey]);

  const clearStoredDraft = useCallback(() => {
    // sessionStorage can throw inside sandboxed iframes — never let
    // draft housekeeping break the page.
    try { sessionStorage.removeItem(draftStorageKey); } catch { /* ignore */ }
  }, [draftStorageKey]);

  /** Restore the mirrored draft when it's newer than the server doc.
   *  Returns true when a draft was adopted (caller must then NOT clobber
   *  it with the server doc); stale/garbage mirrors are dropped. */
  const tryRestoreDraft = useCallback((serverUpdatedAt: number): boolean => {
    try {
      const raw = sessionStorage.getItem(draftStorageKey);
      if (!raw) return false;
      const stored = JSON.parse(raw) as { doc?: ScheduleDoc; savedAt?: number } | null;
      if (!stored?.doc || !Array.isArray(stored.doc.entries) || typeof stored.savedAt !== "number") {
        sessionStorage.removeItem(draftStorageKey);
        return false;
      }
      if (stored.savedAt <= serverUpdatedAt) {
        // The server doc moved past the mirror (saved elsewhere) — stale.
        sessionStorage.removeItem(draftStorageKey);
        return false;
      }
      setDraft(stored.doc);
      setDirty(true);
      setRestoredAt(stored.savedAt);
      return true;
    } catch {
      return false; // storage unavailable — degrade to the server doc
    }
  }, [draftStorageKey]);

  // Refresh schedule + status on a slow interval. The schedule itself
  // changes only on operator action so 15 s is plenty; the status echo
  // can lag the save by ~5–30 s as cloud-connector processes it.
  const refresh = useCallback(async () => {
    const [src, doc, sStatus, oStatus] = await Promise.all([
      adapter.schedule().catch(() => null),
      adapter.getScheduleDoc().catch(() => null),
      adapter.getScheduleStatus().catch(() => null),
      adapter.getOverrideStatus().catch(() => null),
    ]);
    setSource(src);
    setScheduleStatus(sStatus);
    setOverrideStatus(oStatus);
    if (doc) {
      setServerDoc(doc);
      // Pull server state into draft only when there are no unsaved edits.
      // This is the same pattern ems-admin's ControllerSchedulePage uses
      // (line 298 of ControllerSchedulePage.tsx) — a refetch never clobbers
      // in-progress local work.
      if (!dirtyRef.current) {
        // First fetch after mount: a mirrored unsaved draft newer than the
        // server doc wins over the server doc (item 3.5a).
        const restored = !restoreCheckedRef.current && tryRestoreDraft(doc.meta?.updatedAt ?? 0);
        restoreCheckedRef.current = true;
        if (!restored) setDraft(doc);
      }
    } else if (!dirtyRef.current && !hasDraftRef.current) {
      const restored = !restoreCheckedRef.current && tryRestoreDraft(0);
      restoreCheckedRef.current = true;
      if (restored) return;
      // Brand-new site with no schedule yet — initialise a safe shell so
      // the editor renders. The user's first save creates the on-disk doc.
      setDraft({
        version: 1,
        timezone: src?.doc?.timezone ?? "Europe/Rome",
        default: { mode: "gridZeroControl", params: {} },
        entries: [],
        meta: {},
      });
    }
  }, [adapter, tryRestoreDraft]);

  useEffect(() => {
    let alive = true;
    refresh();
    const id = window.setInterval(() => { if (alive) refresh(); }, 15_000);
    return () => { alive = false; window.clearInterval(id); };
  }, [refresh]);

  // F21: reflect the edge echo of the last schedule save into the shared
  // pending-writes store, using the EXACT applied/rejected condition the
  // StatusBanner already evaluates (no new echo logic). When the banner would
  // still read "waiting for edge to confirm…" we leave the write pending.
  useEffect(() => {
    const id = pendingScheduleIdRef.current;
    if (!id) return;
    const localUpdatedAt = lastSavedAtRef.current;
    if (localUpdatedAt === null) return;
    if (scheduleStatus && (scheduleStatus.echo_kind === "rejected" || scheduleStatus.rejected_entries.length > 0)) {
      pendingScheduleIdRef.current = null;
      resolvePending(id, "rejected");
    } else if (scheduleStatus && (scheduleStatus.applied_updatedAt ?? 0) >= localUpdatedAt) {
      pendingScheduleIdRef.current = null;
      resolvePending(id, "applied");
    }
    // else: still pending — StatusBanner shows "waiting for edge…"; leave it.
  }, [scheduleStatus]);

  // (3.5a) Mirror the dirty draft into sessionStorage on every change
  // (cheap — a few KB of JSON). While clean there is nothing to protect;
  // clearing is handled explicitly by save/Discard so a restored-but-
  // unreviewed draft survives further reloads.
  useEffect(() => {
    if (siteSwitchPendingRef.current) {
      // Site just switched: any dirty draft still in memory is the old
      // site's — never write it under the new site's key. The flag drops
      // once the reset effect's state updates have landed.
      if (!dirty && !draft) siteSwitchPendingRef.current = false;
      return;
    }
    if (!dirty || !draft) return;
    try {
      sessionStorage.setItem(draftStorageKey, JSON.stringify({ doc: draft, savedAt: Date.now() }));
    } catch { /* storage unavailable (sandboxed iframe) — in-session guards below still apply */ }
  }, [dirty, draft, draftStorageKey]);

  // (3.5b) Best-effort native guard for standalone browsers. Inside the
  // TB iframe the prompt may be suppressed — the sessionStorage mirror
  // above is the load-bearing protection there.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // (3.5c) In-app dirty guard. BrowserRouter has no useBlocker, so while
  // dirty we intercept internal <a href="/..."> clicks (ViewTabs, Topbar,
  // panel links) at capture phase and confirm before leaving the page.
  // Scoped by destination pathname rather than DOM containment because
  // ViewTabs — the primary leave path — renders INSIDE this page's
  // container; same-path links (param tweaks) pass through untouched.
  // Limitation: command-palette / keyboard-chord navigations call
  // navigate() directly and bypass anchors — acceptable: those are
  // power-user paths, and the sessionStorage mirror restores the draft
  // on return either way (we deliberately keep the mirror on a confirmed
  // leave for the same reason).
  useEffect(() => {
    if (!dirty) return;
    // Inside the cross-origin TB iframe Chromium suppresses window.confirm
    // (it silently returns false), so the guard would preventDefault every
    // internal anchor with no dialog — the tabs would appear frozen. Skip
    // it there: the sessionStorage mirror above already makes leaving safe.
    if (EmbeddedCtxSource.isEmbedded()) return;
    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target instanceof Element ? e.target : null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]") ?? null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return; // new-tab links keep this page alive
      const href = anchor.getAttribute("href") ?? "";
      if (!href.startsWith("/")) return; // external / hash-only links
      const destPath = href.split(/[?#]/, 1)[0];
      if (destPath === window.location.pathname) return; // same-page param links are safe
      if (!window.confirm(tr("manage.schedule.leaveConfirm"))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [dirty]);

  // ─── Draft mutators ─────────────────────────────────────────────────
  // All draft mutators have ZERO deps so they're stable across renders.
  // This is what lets the React.memo wrappers on CalendarGrid / EntryList /
  // DefaultEditor / ActiveStatePanel actually short-circuit re-renders
  // when only the page-level state (busy, editingId, scheduleStatus, …)
  // has changed. Anything they need from current state is read through a
  // ref so the closure stays stable. Without this, every keystroke
  // changed the callback identity and the memo wrappers were a no-op.
  const patchDraft = useCallback((mut: (d: ScheduleDoc) => ScheduleDoc) => {
    setDraft((cur) => (cur ? mut({ ...cur, entries: [...cur.entries] }) : cur));
    setDirty(true);
  }, []);

  const upsertEntry = useCallback((next: ScheduleEntry) => {
    patchDraft((d) => ({
      ...d,
      entries: d.entries.some((e) => e.id === next.id)
        ? d.entries.map((e) => (e.id === next.id ? next : e))
        : [...d.entries, next],
    }));
  }, [patchDraft]);

  const patchEntry = useCallback((id: string, mut: (e: ScheduleEntry) => ScheduleEntry) => {
    patchDraft((d) => ({
      ...d,
      entries: d.entries.map((e) => (e.id === id ? mut(e) : e)),
    }));
  }, [patchDraft]);

  // editingId / newEntryId tracked through refs so the mutators stay stable.
  const editingIdRef = useRef<string | null>(null);
  const newEntryIdRef = useRef<string | null>(null);
  useEffect(() => { editingIdRef.current = editingId; }, [editingId]);
  useEffect(() => { newEntryIdRef.current = newEntryId; }, [newEntryId]);

  const removeEntry = useCallback((id: string) => {
    patchDraft((d) => ({ ...d, entries: d.entries.filter((e) => e.id !== id) }));
    if (editingIdRef.current === id) setEditingId(null);
    if (newEntryIdRef.current === id) setNewEntryId(null);
  }, [patchDraft]);

  // Pre-mint the id outside the updater so we can openthe editor on it
  // without calling setState inside the setState reducer (React may
  // invoke the updater multiple times and the side-effect would fire
  // each time).
  const cloneEntry = useCallback((id: string) => {
    const newId = makeEntryId();
    patchDraft((d) => {
      const src = d.entries.find((e) => e.id === id);
      if (!src) return d;
      const cloned: ScheduleEntry = {
        ...src,
        id: newId,
        name: src.name ? `${src.name} (copy)` : "",
        params: { ...(src.params ?? {}) },
        days_of_week: [...src.days_of_week],
      };
      return { ...d, entries: [...d.entries, cloned] };
    });
    setEditingId(newId);
    setNewEntryId(newId);
  }, [patchDraft]);

  // `opts` lets deep links (?suggest=…, item 2.4) pre-pick the mode and
  // window end; calendar clicks keep the original 60-min PeakShaving stub.
  const addEntryAt = useCallback((day: number, startMin: number, opts?: { mode?: ControllerMode; endMin?: number }) => {
    const newId = makeEntryId();
    const start = Math.max(0, Math.min(1380, startMin));
    const end = Math.min(1440, Math.max(start + 15, opts?.endMin ?? start + 60));
    const mode: ControllerMode = opts?.mode ?? "PeakShaving";
    patchDraft((d) => {
      const entry: ScheduleEntry = {
        id: newId,
        name: "",
        enabled: true,
        priority: Math.max(0, ...d.entries.map((e) => e.priority)) + 1,
        days_of_week: [day],
        start_min: start,
        end_min: end,
        mode,
        params: defaultParamsFor(mode),
        date_start: null,
        date_end: null,
      };
      return { ...d, entries: [...d.entries, entry] };
    });
    setEditingId(newId);
    setNewEntryId(newId);
  }, [patchDraft]);

  const addEntryDefault = useCallback(() => {
    addEntryAt(0, 8 * 60);   // Monday 08:00, 1 h
  }, [addEntryAt]);

  // ─── ?suggest deep link (LINK-07 / MKT-14, item 2.4) ────────────────
  // /schedule?suggest=<ControllerMode>&start_min=<m>&end_min=<m> — the
  // Market-price advisor's "Plan it →" hand-off. Pre-mints a draft entry
  // for today (in the schedule's timezone) via the existing addEntryAt
  // path and opens the editor on it. The params are one-shot: stripped
  // with replace:true so a reload doesn't re-mint, and guarded by a ref
  // so StrictMode double-effects can't double-mint either.
  const [searchParams, setSearchParams] = useSearchParams();
  const suggestHandledRef = useRef(false);
  useEffect(() => {
    if (suggestHandledRef.current || !draft) return;
    const rawMode = searchParams.get("suggest");
    if (!rawMode) return;
    suggestHandledRef.current = true;
    // Number(null) === 0, so empty/missing params must parse to NaN —
    // otherwise a bare ?suggest= link would mint a midnight entry.
    const startParam = searchParams.get("start_min");
    const endParam = searchParams.get("end_min");
    const startRaw = startParam ? Number(startParam) : NaN;
    const endRaw = endParam ? Number(endParam) : NaN;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("suggest");
      next.delete("start_min");
      next.delete("end_min");
      return next;
    }, { replace: true });
    if (!(ORDERED_MODES as readonly string[]).includes(rawMode)) return;
    const start = Number.isFinite(startRaw) ? Math.max(0, Math.min(1380, Math.round(startRaw))) : 8 * 60;
    const end = Number.isFinite(endRaw) ? Math.max(0, Math.min(1440, Math.round(endRaw))) : start + 60;
    const day = projectInTz(draft.timezone, Date.now()).weekday;
    addEntryAt(day, start, { mode: rawMode as ControllerMode, endMin: end });
  }, [draft, searchParams, setSearchParams, addEntryAt]);

  // ─── ?override=1 deep link (CTL-08, item 3.5d) ──────────────────────
  // ControllerPage's "Manage override" hand-off: open the override panel
  // on mount and strip the one-shot param (replace:true) so a reload or
  // shared link doesn't re-open it. Idempotent, so no StrictMode guard
  // is needed (the second effect run sees the param already stripped).
  useEffect(() => {
    if (searchParams.get("override") !== "1") return;
    // R06 — an alarm "Adjust dispatch" lever carries reason + alarmId; prefill
    // the panel's reason and flag the origin so it shows a "from alarm" note.
    const reason = searchParams.get("reason") ?? "";
    const fromAlarm = Boolean(searchParams.get("alarmId"));
    setOverrideOrigin({ reason, fromAlarm });
    setOverrideOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("override");
      next.delete("reason");
      next.delete("alarmId");
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Stable wrappers for child callbacks so the memo'd children
  // (CalendarGrid / EntryList / DefaultEditor / ActiveStatePanel) can
  // skip render when none of their data inputs changed.
  const handlePickEntry = useCallback((id: string) => setEditingId(id), []);
  const handleToggleEnabled = useCallback(
    (id: string) => patchEntry(id, (e) => ({ ...e, enabled: !e.enabled })),
    [patchEntry],
  );
  const handleDefaultChange = useCallback(
    (next: ScheduleDoc["default"]) => patchDraft((d) => ({ ...d, default: next })),
    [patchDraft],
  );
  // Manual open (page button) — clear any alarm origin so a hand-opened panel
  // doesn't inherit a stale prefilled reason from an earlier ?override link.
  const handleOpenOverride = useCallback(() => {
    setOverrideOrigin({ reason: "", fromAlarm: false });
    setOverrideOpen(true);
  }, []);

  // ─── Save / discard ────────────────────────────────────────────────
  const saveDraft = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    setSaveError(null);
    try {
      // Stamp meta.updatedAt before send so the StatusBanner can pair the
      // outbound save with the inbound echo. The adapter sets `updatedAt`
      // on the wire too — that's the field the edge dedups on.
      const stamped: ScheduleDoc = {
        ...draft,
        meta: { ...(draft.meta ?? {}), updatedAt: Date.now() },
      };
      await adapter.saveScheduleDoc(stamped);
      lastSavedAtRef.current = stamped.meta!.updatedAt!;
      // F21: reflect this save into the shared pending-writes store. It stays
      // "pending" until the effect below sees the edge echo apply/reject it
      // (or it decays to "confirmation pending" after the page unmounts).
      pendingScheduleIdRef.current = markPending({
        id: `schedule-${stamped.meta!.updatedAt!}`,
        kind: "schedule",
        label: tr("manage.schedule.bannerSchedule"),
        href: carryTo("/schedule"),
      });
      setDraft(stamped);
      setDirty(false);
      // The save landed — the sessionStorage mirror is now redundant
      // (and older than meta.updatedAt, so it must not restore later).
      clearStoredDraft();
      setRestoredAt(null);
      pushToast({
        level: "ok",
        title: draft.entries.length === 1
          ? tr("manage.schedule.savedToast.one")
          : tr("manage.schedule.savedToast.many", { count: draft.entries.length }),
      });
      // Eager refresh so the status echo + the resolved active mode update
      // without waiting for the next interval tick.
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSaveError(msg);
      pushToast({ level: "critical", title: tr("manage.saveFailed", { error: msg }) });
    } finally {
      setBusy(false);
    }
  }, [adapter, draft, refresh, clearStoredDraft]);

  // Explicit Discard also drops the sessionStorage mirror (item 3.5a) —
  // it's the one deliberate "lose my edits" action. When there is no
  // server doc yet (brand-new site, possibly showing a restored draft)
  // we fall back to the same safe shell refresh() initialises.
  const discardDraft = useCallback(() => {
    clearStoredDraft();
    setRestoredAt(null);
    setDraft(serverDoc ?? {
      version: 1,
      timezone: source?.doc?.timezone ?? "Europe/Rome",
      default: { mode: "gridZeroControl", params: {} },
      entries: [],
      meta: {},
    });
    setDirty(false);
    setEditingId(null);
    setNewEntryId(null);
    setSaveError(null);
  }, [serverDoc, source, clearStoredDraft]);

  // ─── Override actions ─────────────────────────────────────────────
  // F21: override apply/clear resolve synchronously — the adapter.setOverride
  // promise resolving IS the existing success condition (the "applied" toast),
  // a throw IS the existing failure. We mirror that into the pending-writes
  // store with no new echo logic.
  const applyOverride = useCallback(async (p: { mode: ControllerMode; expires_at: number; params: Record<string, number>; reason?: string }) => {
    const pid = markPending({
      id: `override-${Date.now()}`,
      kind: "override",
      label: MODE_CATALOG[p.mode].label,
      href: carryTo("/schedule"),
    });
    try {
      await adapter.setOverride(p);
      resolvePending(pid, "applied");
      pushToast({ level: "ok", title: tr("manage.schedule.overrideApplied", { mode: MODE_CATALOG[p.mode].label }) });
      await refresh();
    } catch (err) {
      resolvePending(pid, "rejected");
      throw err;
    }
  }, [adapter, refresh]);

  const clearOverride = useCallback(async () => {
    const pid = markPending({
      id: `override-clear-${Date.now()}`,
      kind: "override",
      label: tr("manage.schedule.overrideCleared"),
      href: carryTo("/schedule"),
    });
    try {
      await adapter.setOverride(null);
      resolvePending(pid, "applied");
      pushToast({ level: "ok", title: tr("manage.schedule.overrideCleared") });
      await refresh();
    } catch (err) {
      resolvePending(pid, "rejected");
      pushToast({ level: "critical", title: tr("manage.schedule.clearFailed", { error: err instanceof Error ? err.message : String(err) }) });
    }
  }, [adapter, refresh]);

  // ─── Derived ──────────────────────────────────────────────────────
  const editingEntry = useMemo(() => {
    if (!editingId || !draft) return null;
    return draft.entries.find((e) => e.id === editingId) ?? null;
  }, [editingId, draft]);
  const overrideInitMode: ControllerMode = source?.active_now?.mode ?? "PeakShaving";

  // Show the warning if a saved override didn't echo back yet, etc.
  const hasOverride = !!source?.override;

  if (!draft) {
    return (
      <PageShell title={t("manage.schedule.title")} caption={t("manage.schedule.loadingCaption")}>
        <Card className="p-4 text-sm text-text-muted">{t("common.loading")}</Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t("manage.schedule.title")}
      caption={
        <span>
          {t("manage.schedule.caption.pre")}{" "}
          <code className="font-mono">{draft.timezone}</code>
          {t("manage.schedule.caption.post")}
        </span>
      }
    >
      {/* ── Header status row ───────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBanner status={scheduleStatus} localUpdatedAt={lastSavedAtRef.current} label={t("manage.schedule.bannerSchedule")} />
          {hasOverride && (
            <StatusBanner status={overrideStatus} localUpdatedAt={null} label={t("manage.schedule.bannerOverride")} />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => void refresh()}
            disabled={busy}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-edge px-2.5 text-xs text-text-muted hover:bg-surface-2 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3 w-3", busy && "animate-spin")} /> {t("manage.refresh")}
          </button>
          {dirty && (
            <button
              onClick={discardDraft}
              disabled={busy}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-edge px-2.5 text-xs text-text-muted hover:bg-surface-2 disabled:opacity-50"
            >
              <RotateCcw className="h-3 w-3" /> {t("common.discard")}
            </button>
          )}
          {dirty ? (
            <HoldToConfirm
              label={t("manage.schedule.holdToSave")}
              onConfirm={() => void saveDraft()}
            />
          ) : (
            <span className="inline-flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-[11px] text-text-muted">
              <Save className="h-3 w-3" /> {t("manage.schedule.noUnsaved")}
            </span>
          )}
          {/* Reciprocal "look ↔ change" link (NAV-16): the inverse of
              ModeReasoningCard's Controller → Schedule hop. Lets an FM jump to
              the SetpointTracker to confirm the loop tracks after a save. Shown
              only when !dirty so it never competes with an unsaved draft (and
              never trips the capture-phase leave-confirm mid-edit). */}
          {!dirty && (
            <Link
              to={carryTo("/controller")}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-edge px-2.5 text-xs text-text-muted hover:bg-surface-2 hover:border-accent/40"
            >
              {t("manage.schedule.checkController")} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
      {saveError && (
        <div className="card border-critical/40 bg-critical/5 p-3 text-xs text-critical">
          {t("manage.saveFailed", { error: saveError })}
        </div>
      )}

      {/* ── Restored-draft banner (item 3.5a) ───────────────────────── */}
      {restoredAt !== null && (
        <div className="card flex flex-wrap items-center justify-between gap-2 border-warn/40 bg-warn/5 p-3 text-xs text-warn">
          <span>
            {t("manage.schedule.restored", {
              time: new Date(restoredAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={discardDraft}
              disabled={busy}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-warn/40 px-2 text-[11px] text-warn hover:bg-warn/10 disabled:opacity-50"
            >
              <RotateCcw className="h-3 w-3" /> {t("common.discard")}
            </button>
            <button
              onClick={() => setRestoredAt(null)}
              aria-label={t("manage.schedule.restoredDismissAria")}
              title={t("manage.schedule.restoredDismissTitle")}
              className="rounded-md border border-warn/30 p-1 text-warn hover:bg-warn/10"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* ── Active state strip ────────────────────────────────────────── */}
      <ActiveStatePanel
        source={source}
        onOpenOverride={handleOpenOverride}
        onClearOverride={clearOverride}
      />

      {/* ── Mode reference cards (existing pattern, kept) ────────────── */}
      <Card>
        <CardHeader>
          <button
            type="button"
            aria-expanded={modeRefOpen}
            onClick={() => setModeRefOpen((o) => !o)}
            className="flex items-center gap-2 text-left"
          >
            {modeRefOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <CardTitle>{t("manage.schedule.modeReference")}</CardTitle>
          </button>
          <span className="text-[10px] text-text-muted">
            {t("manage.schedule.modeReferenceHint")}
          </span>
        </CardHeader>
        <div className={cn(
          "grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-4",
          !modeRefOpen && "hidden print:grid",
        )}>
          {ORDERED_MODES.map((m) => {
            const info = MODE_CATALOG[m];
            const expanded = expandedDesc === m;
            return (
              <div
                key={m}
                className={cn(
                  "flex cursor-pointer flex-col rounded-md border bg-surface p-3 transition",
                  expanded ? "border-accent shadow-sm" : "border-edge hover:border-accent",
                )}
                onClick={() => setExpandedDesc(expanded ? null : m)}
              >
                <div className="flex items-center justify-between">
                  <span className={cn("rounded border px-2 py-0.5 text-xs font-semibold", MODE_TONE[m])}>
                    {info.label}
                  </span>
                  <Info size={11} className="text-text-faint" />
                </div>
                <div className="mt-2 text-xs text-text-muted">{info.summary}</div>
                {expanded && (
                  <div className="mt-2 border-t border-edge pt-2 text-[11px] leading-relaxed text-text-muted">
                    {info.description}
                    {info.params.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="font-semibold text-text-faint">{t("manage.schedule.parameters")}</div>
                        {info.params.map((p) => (
                          <div key={p.key} className="text-[11px]">
                            <span className="font-mono text-text">{p.key}</span>{" "}
                            <span className="text-text-faint">({p.unit})</span> — {p.hint}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* ── Default mode editor ─────────────────────────────────────── */}
      <DefaultEditor
        doc={draft}
        onChange={handleDefaultChange}
      />

      {/* ── Calendar grid ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t("manage.schedule.weeklyCalendar")}</CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-text-muted">
              {draft.entries.length === 1
                ? t("manage.schedule.calendarHint.one")
                : t("manage.schedule.calendarHint.many", { count: draft.entries.length })}
            </span>
            <button
              onClick={addEntryDefault}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2 text-[11px] text-accent hover:bg-accent/20"
            >
              <Plus className="h-3 w-3" /> {t("manage.schedule.addEntry")}
            </button>
          </div>
        </CardHeader>
        <div className="p-4">
          <CalendarGrid
            doc={draft}
            editingId={editingId}
            onPick={handlePickEntry}
            onPatch={patchEntry}
            onAddAt={addEntryAt}
          />
        </div>
      </Card>

      {/* ── Entry list — collapsible "Table view" disclosure (N10) ──────
          The priority-sorted table mirrors the calendar's entries; collapsed
          by default so the page leads with the answer (active state + calendar)
          instead of opening as a workbench. FORCE-EXPANDED on an empty schedule
          so EntryList's brand-new-site onboarding cue ("click the calendar to
          add an entry") is never hidden. Region toggled with `hidden
          print:block` (mirrors the modeRef print-safe pattern) so a printed
          schedule keeps the priority / date-range columns the calendar can't
          show. */}
      <Card>
        <CardHeader>
          <button
            type="button"
            aria-expanded={tableViewOpen || draft.entries.length === 0}
            aria-controls="schedule-tableview-region"
            onClick={toggleTableView}
            className="flex items-center gap-2 text-left"
          >
            {(tableViewOpen || draft.entries.length === 0) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <CardTitle>{t("manage.schedule.tableView", { count: draft.entries.length })}</CardTitle>
          </button>
          <span className="text-[10px] text-text-muted">
            {t("manage.schedule.entriesHint")}
          </span>
        </CardHeader>
        <div
          id="schedule-tableview-region"
          className={cn(
            "p-2",
            !(tableViewOpen || draft.entries.length === 0) && "hidden print:block",
          )}
        >
          <EntryList
            entries={draft.entries}
            editingId={editingId}
            onEdit={handlePickEntry}
            onClone={cloneEntry}
            onToggleEnabled={handleToggleEnabled}
            onRemove={removeEntry}
          />
        </div>
      </Card>

      {/* ── Modals ──────────────────────────────────────────────────── */}
      {editingEntry && (
        <EntryEditor
          entry={editingEntry}
          timezone={draft.timezone}
          isNew={editingEntry.id === newEntryId}
          dirty={dirty}
          onSave={saveDraft}
          onChange={(next) => {
            // Id edits (only allowed while still new) must RENAME the
            // entry in place — upserting by the NEW id would find no match
            // and append a ghost copy per keystroke, leaving the old-id
            // entry behind. `editingEntry.id` is the current id of the
            // entry being edited; empty ids and ids colliding with a
            // DIFFERENT existing entry are rejected (previous id kept).
            const curId = editingEntry.id;
            if (next.id !== curId) {
              const collides = draft.entries.some((e) => e.id === next.id);
              if (!next.id || collides) {
                upsertEntry({ ...next, id: curId });
                return;
              }
              patchDraft((d) => ({
                ...d,
                entries: d.entries.map((e) => (e.id === curId ? next : e)),
              }));
              setEditingId(next.id);
              if (newEntryId === curId) setNewEntryId(next.id);
              return;
            }
            upsertEntry(next);
          }}
          onClone={() => cloneEntry(editingEntry.id)}
          onRemove={() => removeEntry(editingEntry.id)}
          onClose={() => {
            setEditingId(null);
            setNewEntryId(null);
          }}
        />
      )}

      <OverridePanel
        open={overrideOpen}
        initialMode={overrideInitMode}
        initialReason={overrideOrigin.reason}
        fromAlarm={overrideOrigin.fromAlarm}
        onApply={applyOverride}
        onClose={() => setOverrideOpen(false)}
      />
    </PageShell>
  );
}
